'use client';

import { Button, Menu, MenuItem } from '@mui/material';
import React, { startTransition, useCallback, useId, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from './navigation';
import { useCurrentLocale } from './LocaleProvider';
import { getSupportedLanguages } from './localeMapping';

export function LanguageSelector() {
  const { region } = useCurrentLocale();
  const languages = getSupportedLanguages(region);
  const router = useRouter();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLocale = useCallback(
    (locale: string) => {
      startTransition(() => {
        router.replace(`/`, { locale });
        router.refresh(); // clear caching to make sure the locale cookie is set correctly
      });
    },
    [router]
  );

  const t = useTranslations('Localization');

  const id = useId();
  const buttonId = `language-selector-button-${id}`;
  const menuId = `language-selector-menu-${id}`;

  if (languages.length <= 1) return null; // no point in displaying the lang selector if there's only one option

  return (
    <>
      <Button
        variant={'outlined'}
        color={'secondary'}
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{ minWidth: '8rem' }}
      >
        {t('language')}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
      >
        {languages.map(lang => (
          <MenuItem
            key={lang}
            onClick={e => {
              e.preventDefault();
              handleClose();
              handleLocale(lang);
            }}
            sx={{ minWidth: '8rem', justifyContent: 'center' }}
          >
            {t(lang as never)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
